/**
 * MaxPrepsTeamInfo — Collapsible dropdowns showing Record, Schedule, and Staff
 * for a team scraped from MaxPreps. Displayed below the video player on game pages
 * when StatMoose GameCast is not attached.
 */
import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, Calendar, Trophy, Users, ExternalLink, Loader2, AlertCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── Collapsible Section ── */
const Section = ({ title, icon: Icon, children, defaultOpen = false, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 bg-zinc-900/60 overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <span className="flex items-center gap-2 text-xs font-bold text-white uppercase tracking-widest">
          <Icon className="w-4 h-4 text-zinc-400" />
          {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && <div className="px-4 pb-4 border-t border-white/5">{children}</div>}
    </div>
  );
};

/* ── Record ── */
const RecordContent = ({ record, rankings }) => {
  if (!record || Object.keys(record).length === 0) {
    return <p className="text-zinc-500 text-xs py-2">No record data available.</p>;
  }

  const statBoxes = [
    { label: "Overall", value: record.overall },
    { label: record.district_name ? "District" : "Conference", value: record.district },
    { label: "Home", value: record.home },
    { label: "Away", value: record.away },
  ].filter(b => b.value);

  return (
    <div className="pt-3 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {statBoxes.map((b, i) => (
          <div key={i} className="bg-black/40 p-3 text-center">
            <div className="text-white text-base font-bold font-mono">{b.value}</div>
            <div className="text-zinc-500 text-[10px] uppercase tracking-wider mt-0.5">{b.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
        {record.points_for && <span>PF: <strong className="text-white">{record.points_for}</strong></span>}
        {record.points_against && <span>PA: <strong className="text-white">{record.points_against}</strong></span>}
        {record.streak && <span>Streak: <strong className="text-white">{record.streak}</strong></span>}
        {record.district_name && (
          <span className="text-zinc-500">{record.district_name}{record.district_placement ? ` (${record.district_placement})` : ""}</span>
        )}
      </div>

      {rankings && rankings.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5">Rankings</p>
          <div className="flex flex-wrap gap-2">
            {rankings.map((r, i) => (
              <span key={i} className="bg-zinc-800/80 px-2 py-1 text-xs text-zinc-300 inline-flex items-center gap-1.5">
                {r.context}: <strong className="text-white">#{r.rank}</strong>
                {r.movement && r.movement !== "0" && r.movement !== "" && (
                  r.movement.startsWith("-")
                    ? <TrendingDown className="w-3 h-3 text-red-400" />
                    : <TrendingUp className="w-3 h-3 text-green-400" />
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Schedule (mini — up to 3 games from home page) ── */
const ScheduleContent = ({ schedule, teamName }) => {
  if (!schedule || schedule.length === 0) {
    return <p className="text-zinc-500 text-xs py-2">No schedule data available.</p>;
  }

  return (
    <div className="pt-3 space-y-2" data-testid="maxpreps-schedule-list">
      {schedule.map((game, i) => {
        // Determine which side is the opponent
        const isHome = game.home_team?.name === teamName;
        const team = isHome ? game.home_team : game.away_team;
        const opp = isHome ? game.away_team : game.home_team;
        const prefix = isHome ? "vs" : "@";

        // Format date
        let dateStr = game.date || "";
        if (dateStr && dateStr.includes("-")) {
          const [y, m, d] = dateStr.split("-");
          dateStr = `${parseInt(m)}/${parseInt(d)}`;
        }

        return (
          <div
            key={i}
            className="flex items-center gap-3 bg-black/30 px-3 py-2.5 hover:bg-white/5 transition-colors"
          >
            <span className="text-zinc-500 text-xs w-10 shrink-0 font-mono">{dateStr}</span>
            <span className="text-zinc-600 text-[10px] w-5 shrink-0 text-center">{prefix}</span>
            {opp.mascot_url && (
              <img src={opp.mascot_url} alt="" className="w-5 h-5 object-contain shrink-0" />
            )}
            <span className="text-white text-xs flex-1 min-w-0">{opp.name}</span>
            {game.is_completed ? (
              <span className={`text-xs font-bold font-mono ${team.result === "W" ? "text-green-400" : "text-red-400"}`}>
                {team.result} {team.score}-{opp.score}
              </span>
            ) : game.is_live ? (
              <span className="text-xs font-bold text-yellow-400 animate-pulse">LIVE</span>
            ) : (
              <span className="text-zinc-600 text-xs">—</span>
            )}
          </div>
        );
      })}
      <p className="text-[10px] text-zinc-600 text-center pt-1">Showing recent & upcoming games</p>
    </div>
  );
};

/* ── Staff (coaches) ── */
const StaffContent = ({ staff }) => {
  if (!staff || staff.length === 0) {
    return <p className="text-zinc-500 text-xs py-2">No staff data available.</p>;
  }
  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {staff.map((s, i) => (
          <div key={i} className="flex items-center gap-2 bg-black/30 px-3 py-2.5">
            <div className="w-7 h-7 bg-zinc-800 flex items-center justify-center text-zinc-500 text-[10px] font-bold shrink-0">
              {s.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[11px] font-bold leading-tight">{s.name}</div>
              <div className="text-zinc-500 text-[10px] leading-tight">{s.role}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Single Team Block ── */
const TeamInfoBlock = ({ link, label, teamColor, adminLogoUrl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!link) { setLoading(false); return; }
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/maxpreps/team`, { params: { link } });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Failed to load team info");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [link]);

  if (!link) return null;

  if (loading) {
    return (
      <div className="border border-white/10 bg-zinc-900/60 p-6 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
        <span className="text-zinc-500 text-xs">Loading {label} info from MaxPreps...</span>
      </div>
    );
  }

  if (error || data?.error) {
    return (
      <div className="border border-white/10 bg-zinc-900/60 p-6 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-red-500/70" />
        <span className="text-zinc-500 text-xs">Unable to load data: {error || data?.error}</span>
      </div>
    );
  }

  if (!data) return null;

  const teamName = data.team_info?.name || label;
  const accentColor = data.team_info?.color ? `#${data.team_info.color}` : (teamColor || "#3b82f6");
  const logoUrl = adminLogoUrl || data.team_info?.logo_url;

  return (
    <div className="space-y-px" data-testid={`maxpreps-team-block-${label.toLowerCase().replace(/\s/g, "-")}`}>
      {/* Team Header */}
      <div className="flex items-center gap-3 px-3 py-3 bg-zinc-900/80 border border-white/10 border-b-0"
        style={{ borderLeftColor: accentColor, borderLeftWidth: "3px" }}
      >
        {logoUrl && (
          <img src={logoUrl} alt="" className="w-8 h-8 object-contain" />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-white text-sm font-bold truncate">{teamName}</h3>
          {data.team_info?.location && (
            <p className="text-zinc-500 text-[10px]">{data.team_info.location}</p>
          )}
        </div>
        <a
          href={data.maxpreps_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-zinc-600 flex items-center gap-1 text-[10px] shrink-0"
          data-testid="maxpreps-external-link"
        >
          MaxPreps <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Collapsible Sections */}
      <Section title="Record" icon={Trophy} defaultOpen={true} testId={`maxpreps-record-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <RecordContent record={data.record} rankings={data.rankings} />
      </Section>
      <Section title="Schedule" icon={Calendar} testId={`maxpreps-schedule-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <ScheduleContent schedule={data.schedule} teamName={data.team_info?.name} />
      </Section>
      <Section title="Coaching Staff" icon={Users} testId={`maxpreps-staff-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <StaffContent staff={data.staff} />
      </Section>
    </div>
  );
};

/* ── Main Export ── */
export const MaxPrepsTeamInfo = ({ broadcast }) => {
  const homeLink = broadcast?.home_maxpreps_link;
  const awayLink = broadcast?.away_maxpreps_link;

  if (!homeLink && !awayLink) return null;

  const homeTeam = typeof broadcast.home_team === "object" ? broadcast.home_team : null;
  const awayTeam = typeof broadcast.away_team === "object" ? broadcast.away_team : null;

  return (
    <div className="space-y-4" data-testid="maxpreps-team-info-section">
      <div className="flex items-center gap-2 px-1">
        <div className="w-1 h-4 bg-white" />
        <h3 className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Team Info via MaxPreps</h3>
      </div>
      <div className="grid grid-cols-1 gap-6">
        {homeLink && (
          <TeamInfoBlock
            link={homeLink}
            label={homeTeam?.name || "Home"}
            teamColor={homeTeam?.primary_color || homeTeam?.color}
            adminLogoUrl={homeTeam?.logo_url}
          />
        )}
        {awayLink && (
          <TeamInfoBlock
            link={awayLink}
            label={awayTeam?.name || "Away"}
            teamColor={awayTeam?.primary_color || awayTeam?.color}
            adminLogoUrl={awayTeam?.logo_url}
          />
        )}
      </div>
    </div>
  );
};

export default MaxPrepsTeamInfo;
